import React, { useState, useEffect } from "react";
import { getDashboardStats, getUsers } from "../../Web Apis/webApis";
import Load from "../Load/Load";
import { message } from "antd";
import { useNavigate } from "react-router";

const Home = () => {
  const [loading, setLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({});
  const [userData, setUserData] = useState([]);

  const navigate = useNavigate();

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);

      const response = await getDashboardStats();

      if (response?.data?.success) {
        console.log("dashboard stats", response?.data?.data);
        setDashboardStats(response?.data?.data);
      }
    } catch (error) {
      console.log("error fetching dashboard stats", error.message);
      if (
        error.message === "Network Error" ||
        error.message.includes("ERR_INTERNET_DISCONNECTED")
      ) {
        // Handle internet disconnection
        return;
      }
      // Handle specific error cases
      if (error?.response?.status === 401) {
        window.alert(
          "Your account is logged in to another device, please login again !",
        );
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
      if (error?.response?.status === 500) {
        message.error("Server not responding");
      } else {
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  // Listen for back button and redirect to /home
  useEffect(() => {
    const handlePopState = () => {
      navigate("/home"); // Redirect to /home when the back button is pressed
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  return (
    <div className="p-4 mt-16">
      <h2 className="text-3xl font-bold text-green-800">Dashboard</h2>
      <p className="text-red-400">Welcome to the admin dashboard!</p>

      {/* Dashboard Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 my-4">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg rounded-lg p-6 hover:shadow-2xl transition-shadow duration-300">
          <h3 className="text-lg font-semibold">Total Users</h3>
          <p className="text-4xl font-bold">
            {dashboardStats?.overview?.totalUsers || 0}
          </p>
          <p className="mt-2 text-sm">Total registered users</p>
        </div>

        <div className="bg-gradient-to-r from-green-600 to-green-800 text-white shadow-lg rounded-lg p-6 hover:shadow-2xl transition-shadow duration-300">
          <h3 className="text-lg font-semibold">Total Admins</h3>
          <p className="text-4xl font-bold">
            {dashboardStats?.overview?.totalAdmins || 0}
          </p>
          <p className="mt-2 text-sm">Total admin users</p>
        </div>

        <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white shadow-lg rounded-lg p-6 hover:shadow-2xl transition-shadow duration-300">
          <h3 className="text-lg font-semibold">Total Contents</h3>
          <p className="text-4xl font-bold">
            {dashboardStats?.overview?.totalContents || 0}
          </p>
          <p className="mt-2 text-sm">Total content items</p>
        </div>

        <div className="bg-gradient-to-r from-orange-600 to-orange-800 text-white shadow-lg rounded-lg p-6 hover:shadow-2xl transition-shadow duration-300">
          <h3 className="text-lg font-semibold">Published Contents</h3>
          <p className="text-4xl font-bold">
            {dashboardStats?.overview?.publishedContents || 0}
          </p>
          <p className="mt-2 text-sm">Published content items</p>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-800 text-white shadow-lg rounded-lg p-6 hover:shadow-2xl transition-shadow duration-300">
          <h3 className="text-lg font-semibold">Total Stories</h3>
          <p className="text-4xl font-bold">
            {dashboardStats?.overview?.totalStories || 0}
          </p>
          <p className="mt-2 text-sm">Total AI-generated stories</p>
        </div>

        <div className="bg-gradient-to-r from-pink-600 to-pink-800 text-white shadow-lg rounded-lg p-6 hover:shadow-2xl transition-shadow duration-300">
          <h3 className="text-lg font-semibold">Help Requests</h3>
          <p className="text-4xl font-bold">
            {dashboardStats?.overview?.totalHelpRequests || 0}
          </p>
          <p className="mt-2 text-sm">Total support requests</p>
        </div>

        <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white shadow-lg rounded-lg p-6 hover:shadow-2xl transition-shadow duration-300">
          <h3 className="text-lg font-semibold">Users (Last 7 Days)</h3>
          <p className="text-4xl font-bold">
            {dashboardStats?.recentActivity?.usersLast7Days || 0}
          </p>
          <p className="mt-2 text-sm">New users this week</p>
        </div>

        <div className="bg-gradient-to-r from-yellow-600 to-yellow-800 text-white shadow-lg rounded-lg p-6 hover:shadow-2xl transition-shadow duration-300">
          <h3 className="text-lg font-semibold">Stories (Last 7 Days)</h3>
          <p className="text-4xl font-bold">
            {dashboardStats?.recentActivity?.storiesLast7Days || 0}
          </p>
          <p className="mt-2 text-sm">Stories generated this week</p>
        </div>
      </div>

      {/* Recent Activity Section */}
      {dashboardStats?.recentActivity && (
        <div className="mt-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Recent Activity
          </h3>
          <div className="bg-white shadow-lg rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-600">Users (7 days)</p>
                <p className="text-2xl font-bold text-blue-600">
                  {dashboardStats.recentActivity.usersLast7Days || 0}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Contents (7 days)</p>
                <p className="text-2xl font-bold text-green-600">
                  {dashboardStats.recentActivity.contentsLast7Days || 0}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Stories (7 days)</p>
                <p className="text-2xl font-bold text-purple-600">
                  {dashboardStats.recentActivity.storiesLast7Days || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <Load />}
    </div>
  );
};

export default Home;
